import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  Avatar,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Divider,
  Card,
  CardContent,
} from '@mui/material';
import {
  Visibility,
  ContentCopy,
  TrendingUp,
  TrendingDown,
  AccountBalance,
  ShowChart,
  Rule as RuleIcon,
  Lock as LockIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { Portfolio, Position, SharedContent } from '../types';
import { PortfolioService } from '../services/portfolioService';
import { PositionService } from '../services/positionService';
import { SharedContentService } from '../services/sharedContentService';
import { StockService } from '../services/stockService';
import { formatCurrency, formatPercent } from '../utils/formatters';
import { useUser } from '../contexts/UserContext';

interface SharedPortfolioViewProps {
  sharedContentId: string;
  onClose?: () => void;
  onImportStock?: (symbol: string) => void;
}

const SharedPortfolioView: React.FC<SharedPortfolioViewProps> = ({
  sharedContentId,
  onClose,
  onImportStock,
}) => {
  const { user } = useUser();
  const [loading, setLoading] = useState(true);
  const [sharedContent, setSharedContent] = useState<SharedContent | null>(null);
  const [portfolio, setPortfolio] = useState<Portfolio | null>(null);
  const [positions, setPositions] = useState<Position[]>([]);
  const [closedPositions, setClosedPositions] = useState<Position[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [importing, setImporting] = useState<string | null>(null);

  useEffect(() => {
    loadSharedPortfolio();
    // Increment view count
    if (sharedContentId) {
      SharedContentService.incrementViewCount(sharedContentId).catch(console.error);
    }
  }, [sharedContentId]);

  const loadSharedPortfolio = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get shared content metadata
      const shared = await SharedContentService.getSharedContentById(sharedContentId);
      if (!shared || !shared.portfolioId) {
        throw new Error('Shared portfolio not found');
      }
      setSharedContent(shared);

      // Get portfolio
      const portfolioData = await PortfolioService.getPortfolio(shared.portfolioId);
      if (!portfolioData) {
        throw new Error('Portfolio not found');
      }
      setPortfolio(portfolioData);

      // Get positions if allowed
      if (portfolioData.sharedSettings?.showHoldings) {
        const openPositions = await PositionService.getOpenPositions(portfolioData.id);
        setPositions(openPositions);
        
        // Also get closed positions for transaction history
        const closed = await PositionService.getClosedPositions(portfolioData.id);
        setClosedPositions(closed);
      }
    } catch (error) {
      console.error('Error loading shared portfolio:', error);
      setError('Failed to load shared portfolio');
    } finally {
      setLoading(false);
    }
  };

  const handleImportStock = async (symbol: string) => {
    if (!user || !onImportStock) return;

    try {
      setImporting(symbol);
      setError(null);

      // Call the parent's import function
      onImportStock(symbol);

      // Increment copy count
      await SharedContentService.incrementCopyCount(sharedContentId);
    } catch (error) {
      console.error('Error importing stock:', error);
      setError('Failed to import stock');
    } finally {
      setImporting(null);
    }
  };

  const calculateTotalReturn = () => {
    if (!portfolio) return 0;
    // Total Return = Total Earnings / Initial Fund Value * 100
    return (portfolio.totalEarnings / portfolio.initialFundValue) * 100;
  };

  const calculateUnrealizedGainLoss = () => {
    if (!positions || positions.length === 0) return 0;
    return positions.reduce((total, position) => {
      return total + (position.quantity * (position.currentPrice - position.entryPrice));
    }, 0);
  };

  const calculateWinRate = () => {
    if (closedPositions.length === 0) return 0;
    
    const winners = closedPositions.filter(p => {
      if (!p.exitPrice || !p.entryPrice) return false;
      return p.exitPrice > p.entryPrice;
    });
    
    return (winners.length / closedPositions.length) * 100;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !portfolio || !sharedContent) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error || 'Portfolio not found'}
      </Alert>
    );
  }

  const showHoldings = portfolio.sharedSettings?.showHoldings;
  const showPerformance = portfolio.sharedSettings?.showPerformance;
  const showRules = portfolio.sharedSettings?.showRules;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" gutterBottom>
            {portfolio.name}
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <Stack direction="row" spacing={1} alignItems="center">
              <Avatar src={sharedContent.ownerProfilePic} sx={{ width: 24, height: 24 }}>
                {sharedContent.ownerDisplayName[0]}
              </Avatar>
              <Typography variant="body2">
                {sharedContent.ownerDisplayName}
                {sharedContent.ownerHandle && (
                  <Typography component="span" variant="caption" color="text.secondary">
                    {' '}@{sharedContent.ownerHandle}
                  </Typography>
                )}
              </Typography>
            </Stack>
            <Divider orientation="vertical" flexItem />
            <Stack direction="row" spacing={1}>
              <Chip
                icon={<Visibility />}
                label={sharedContent.viewCount}
                size="small"
                variant="outlined"
              />
              <Chip
                icon={<ContentCopy />}
                label={sharedContent.copyCount}
                size="small"
                variant="outlined"
              />
            </Stack>
          </Stack>
        </Box>
        <Chip
          label={portfolio.type}
          color={portfolio.type === 'real' ? 'success' : 'primary'}
          icon={<AccountBalance />}
        />
      </Stack>

      {/* Portfolio Overview */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Portfolio Overview
          </Typography>
          <Stack direction="row" spacing={4} flexWrap="wrap">
            <Box>
              <Typography variant="caption" color="text.secondary">
                Created
              </Typography>
              <Typography variant="body1">
                {format(portfolio.createdAt, 'MMM d, yyyy')}
              </Typography>
            </Box>
            {showPerformance ? (
              <>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Initial Value
                  </Typography>
                  <Typography variant="body1">
                    {formatCurrency(portfolio.initialFundValue)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Current Value
                  </Typography>
                  <Typography variant="body1">
                    {formatCurrency(portfolio.currentValue)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Total Earnings
                  </Typography>
                  <Typography
                    variant="body1"
                    color={portfolio.totalEarnings >= 0 ? 'success.main' : 'error.main'}
                  >
                    {portfolio.totalEarnings >= 0 ? '+' : ''}{formatCurrency(portfolio.totalEarnings)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Unrealized G/L
                  </Typography>
                  <Typography
                    variant="body1"
                    color={calculateUnrealizedGainLoss() >= 0 ? 'success.main' : 'error.main'}
                  >
                    {calculateUnrealizedGainLoss() >= 0 ? '+' : ''}{formatCurrency(calculateUnrealizedGainLoss())}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" title="Total percentage gain/loss including realized and unrealized">
                    Total Return
                  </Typography>
                  <Typography
                    variant="body1"
                    color={calculateTotalReturn() >= 0 ? 'success.main' : 'error.main'}
                  >
                    {calculateTotalReturn() >= 0 ? '+' : ''}{formatPercent(calculateTotalReturn())}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" title="Percentage of winning trades">
                    Win Rate
                  </Typography>
                  <Typography variant="body1">
                    {formatPercent(calculateWinRate())} ({closedPositions.filter(p => p.exitPrice && p.entryPrice && p.exitPrice > p.entryPrice).length}/{closedPositions.length})
                  </Typography>
                </Box>
              </>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LockIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  Performance data is private
                </Typography>
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Current Holdings */}
      {showHoldings ? (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Current Holdings ({positions.length})
          </Typography>
          {positions.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Symbol</TableCell>
                    <TableCell align="right">Shares</TableCell>
                    <TableCell align="right">Entry Price</TableCell>
                    <TableCell align="right">Current Price</TableCell>
                    <TableCell align="right">Gain/Loss</TableCell>
                    <TableCell align="right">Value</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {positions.map((position) => {
                    const totalValue = position.quantity * position.currentPrice;
                    const gainLoss = position.quantity * (position.currentPrice - position.entryPrice);
                    const gainLossPercent = ((position.currentPrice - position.entryPrice) / position.entryPrice) * 100;

                    return (
                      <TableRow key={position.id}>
                        <TableCell>
                          <Typography variant="subtitle2">
                            {position.symbol}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {position.quantity}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(position.entryPrice)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(position.currentPrice)}
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="column" alignItems="flex-end">
                            <Typography
                              variant="body2"
                              color={gainLoss >= 0 ? 'success.main' : 'error.main'}
                            >
                              {gainLoss >= 0 ? '+' : ''}{formatCurrency(gainLoss)}
                            </Typography>
                            <Typography
                              variant="caption"
                              color={gainLoss >= 0 ? 'success.main' : 'error.main'}
                            >
                              {gainLoss >= 0 ? '+' : ''}{formatPercent(gainLossPercent)}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(totalValue)}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Add to Watchlist">
                            <IconButton
                              size="small"
                              onClick={() => handleImportStock(position.symbol)}
                              disabled={importing === position.symbol}
                            >
                              {importing === position.symbol ? (
                                <CircularProgress size={20} />
                              ) : (
                                <AddIcon />
                              )}
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No open positions
            </Typography>
          )}
        </Paper>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center', mb: 3 }}>
          <LockIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Holdings are Private
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The owner has chosen not to share their holdings
          </Typography>
        </Paper>
      )}

      {/* Transaction History (Exits) */}
      {showHoldings && closedPositions.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Transaction History ({closedPositions.length} Exits)
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Symbol</TableCell>
                  <TableCell align="right">Entry Date</TableCell>
                  <TableCell align="right">Exit Date</TableCell>
                  <TableCell align="right">Entry Price</TableCell>
                  <TableCell align="right">Exit Price</TableCell>
                  <TableCell align="right">Shares</TableCell>
                  <TableCell align="right">P/L</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {closedPositions.slice(0, 10).map((position) => {
                  const pl = position.exitPrice && position.entryPrice
                    ? position.quantity * (position.exitPrice - position.entryPrice)
                    : 0;
                  const plPercent = position.exitPrice && position.entryPrice
                    ? ((position.exitPrice - position.entryPrice) / position.entryPrice) * 100
                    : 0;

                  return (
                    <TableRow key={position.id}>
                      <TableCell>
                        <Typography variant="subtitle2">
                          {position.symbol}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {format(position.entryDate, 'MMM d')}
                      </TableCell>
                      <TableCell align="right">
                        {position.exitDate && format(position.exitDate, 'MMM d')}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(position.entryPrice)}
                      </TableCell>
                      <TableCell align="right">
                        {position.exitPrice && formatCurrency(position.exitPrice)}
                      </TableCell>
                      <TableCell align="right">
                        {position.quantity}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="column" alignItems="flex-end">
                          <Typography
                            variant="body2"
                            color={pl >= 0 ? 'success.main' : 'error.main'}
                          >
                            {pl >= 0 ? '+' : ''}{formatCurrency(pl)}
                          </Typography>
                          <Typography
                            variant="caption"
                            color={pl >= 0 ? 'success.main' : 'error.main'}
                          >
                            {pl >= 0 ? '+' : ''}{formatPercent(plPercent)}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Add to Watchlist">
                          <IconButton
                            size="small"
                            onClick={() => handleImportStock(position.symbol)}
                            disabled={importing === position.symbol}
                          >
                            {importing === position.symbol ? (
                              <CircularProgress size={20} />
                            ) : (
                              <AddIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {closedPositions.length > 10 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
              Showing 10 most recent exits
            </Typography>
          )}
        </Paper>
      )}

      {/* Trading Rules */}
      {showRules && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <RuleIcon />
              <Typography variant="h6">Trading Rules</Typography>
            </Stack>
            <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1 }}>
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Max Positions
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {portfolio.maxPositions}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Position Size
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {formatCurrency(portfolio.positionSize)}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Risk per Position
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    8-10% stop loss
                  </Typography>
                </Stack>
                {portfolio.type === 'virtual' && portfolio.autopilotEnabled !== undefined && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Autopilot Trading
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {portfolio.autopilotEnabled ? 'Enabled' : 'Disabled'}
                    </Typography>
                  </Stack>
                )}
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Trading Strategy
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    Growth momentum stocks
                  </Typography>
                </Stack>
              </Stack>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              * Standard rules apply. Individual positions may vary based on market conditions.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Info Box */}
      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          This is a read-only view of a shared portfolio. You can add any stock from this portfolio 
          to your watchlist by clicking the add button.
        </Typography>
      </Alert>
    </Box>
  );
};

export default SharedPortfolioView;